import type { VehicleSummaryTO, VehicleTO } from '@control-center/control-center-client-public-vehicles-ts';
import {
    AdsStateField,
    ConnectivityStatusField,
    DeviationField,
    DriverModeField,
    DrivingStateField,
    MrmStateField,
    PositionField,
    SpeedMsField,
    VehicleSubtype as WireVehicleSubtype,
    VehicleType as WireVehicleType,
} from '@control-center/control-center-client-public-vehicles-ts/dist/schemas';
import { catchWithLog } from 'helper/zod';
import { z } from 'zod';

import type { FieldError, FieldErrorType, MonitoredField } from '../monitored';
import type {
    AdsState,
    ConnectivityDetails,
    Deviation,
    DeviationSeverity,
    DeviationState,
    DriverMode,
    DrivingState,
    GeoPosition,
    MrmDetails,
    MrmState,
    Vehicle,
    VehicleOwner,
    VehicleSubtype,
    VehicleSummary,
    VehicleType,
} from '../vehicle';

export type WireVehicle = VehicleTO;
export type WireVehicleSummary = VehicleSummaryTO;

const PARSING_ERROR_TYPE = 'ERROR_TYPE_PARSING_ERROR';

const parsingErrorField = () => ({ error: { type: PARSING_ERROR_TYPE, updated_at: new Date().toISOString() } });

const safeOptional = <S extends z.ZodTypeAny>(schema: S, label: string) =>
    catchWithLog(schema.nullish(), undefined as unknown as z.infer<S> | null | undefined, `vehicleFromRestV2.${label}`);

const safeField = <S extends z.ZodTypeAny>(schema: S, label: string) =>
    catchWithLog(
        schema.nullish(),
        parsingErrorField as unknown as () => z.infer<S> | null | undefined,
        `vehicleFromRestV2.${label}`
    );

const DATE_KEYS = new Set([
    'updated_at',
    'sent_at',
    'raised_at',
    'latest_vehicle_update',
    'timestamp',
    'started_at',
    'resolved_at',
]);

const sanitizeWire = (input: unknown): unknown => {
    if (Array.isArray(input)) {
        return input.map(sanitizeWire);
    }
    if (input && typeof input === 'object') {
        const out: Record<string, unknown> = {};
        for (const [key, value] of Object.entries(input)) {
            if (value === null) {
                continue;
            }
            if (typeof value === 'string' && DATE_KEYS.has(key)) {
                const parsed = new Date(value);
                out[key] = Number.isNaN(parsed.getTime()) ? value : parsed.toISOString();
            } else {
                out[key] = sanitizeWire(value);
            }
        }
        return out;
    }
    return input;
};

export const WireVehicleSchema = z.preprocess(
    sanitizeWire,
    z.object({
        vin: z.string().min(1),
        name: safeOptional(z.string(), 'name'),
        timestamp: safeOptional(z.string(), 'timestamp'),
        license_plate: safeOptional(z.string(), 'license_plate'),
        fleet_id: safeOptional(z.string(), 'fleet_id'),
        active_mission_id: safeOptional(z.string(), 'active_mission_id'),
        latest_vehicle_update: safeOptional(z.string(), 'latest_vehicle_update'),
        owners: safeOptional(z.array(z.record(z.string(), z.unknown())), 'owners'),
        ads_state: safeField(AdsStateField, 'ads_state'),
        connectivity_status: safeField(ConnectivityStatusField, 'connectivity_status'),
        driving_state: safeField(DrivingStateField, 'driving_state'),
        driver_mode: safeField(DriverModeField, 'driver_mode'),
        position: safeField(PositionField, 'position'),
        speed_ms: safeField(SpeedMsField, 'speed_ms'),
        mrm_state: safeField(MrmStateField, 'mrm_state'),
        deviations: z.array(safeField(DeviationField, 'deviations')).nullish().catch(undefined),
    })
);

export const WireVehicleSummarySchema = z.preprocess(
    sanitizeWire,
    z.object({
        vin: z.string().min(1),
        name: safeOptional(z.string(), 'name'),
        license_plate: safeOptional(z.string(), 'license_plate'),
        connectivity_status: safeField(ConnectivityStatusField, 'connectivity_status').nullish().catch(undefined),
        fleet_id: safeOptional(z.string(), 'fleet_id'),
        type: safeField(WireVehicleType, 'type'),
        subtype: safeField(WireVehicleSubtype, 'subtype'),
        created_at: safeOptional(z.string(), 'created_at'),
        created_by: safeOptional(z.string(), 'created_by'),
    })
);

const ADS_STATES = ['AUTONOMOUS', 'SHADOW', 'NOT_AVAILABLE', 'UNKNOWN'] as const;
const CONNECTIVITY_STATES = ['ONLINE', 'OFFLINE', 'UNKNOWN'] as const;
const DRIVING_STATES = ['DRIVING', 'IDLE', 'PARKED', 'REVERSING', 'UNKNOWN'] as const;
const DRIVER_MODES = ['IN_SUPERVISED', 'OUT_AUTOMATED', 'UNKNOWN'] as const;
const DEVIATION_SEVERITIES = ['FATAL', 'ERROR', 'WARNING', 'INFO', 'UNKNOWN'] as const;
const DEVIATION_STATES = ['ACTIVE', 'RESOLVED', 'UNKNOWN'] as const;
const MRM_STATES = ['EXECUTING_MRM', 'MRC', 'NONE', 'RELEASING_MRC', 'MRM_FAULTED', 'MRM_ERROR', 'UNKNOWN'] as const;
const VEHICLE_TYPES = ['UNKNOWN', 'DUMMY', 'SIMULATOR', 'TRATON'] as const;
const VEHICLE_SUBTYPES = ['UNKNOWN', 'PHYSICAL_VEHICLE', 'HW_RIG', 'SIMULATED_VEHICLE_WITH_CONNECTIVITY'] as const;

const ADS_STATE_PREFIX = 'AUTONOMOUS_SYSTEM_STATE_';
const CONNECTIVITY_STATE_PREFIX = 'CONNECTIVITY_STATE_';
const DRIVING_STATE_PREFIX = 'DRIVING_STATE_';
const DRIVER_MODE_PREFIX = 'DRIVER_MODE_';
const SEVERITY_PREFIX = 'SEVERITY_';
const DEVIATION_STATE_PREFIX = 'DEVIATION_STATE_';
const MRM_STATE_PREFIX = 'MRM_STATE_';
const FIELD_ERROR_TYPE_PREFIX = 'ERROR_TYPE_';

export const KNOWN_FIELD_ERROR_TYPES: readonly string[] = [
    'UNSPECIFIED',
    'UNKNOWN',
    'UNAVAILABLE',
    'TIMEOUT',
    'NOT_CONFIGURED',
    'PARSING_ERROR',
];

const fromEnum = <T extends string>(raw: unknown, prefix: string, known: readonly T[]): T => {
    if (typeof raw !== 'string' || !raw.startsWith(prefix)) {
        return 'UNKNOWN' as T;
    }
    const member = raw.slice(prefix.length);
    return (known as readonly string[]).includes(member) ? (member as T) : ('UNKNOWN' as T);
};

const fromPlainEnum = <T extends string>(raw: unknown, known: readonly T[]): T => {
    if (typeof raw !== 'string') {
        return 'UNKNOWN' as T;
    }
    return (known as readonly string[]).includes(raw) ? (raw as T) : ('UNKNOWN' as T);
};

export const toDate = (raw: unknown): Date | undefined => {
    if (typeof raw !== 'string' || raw.trim() === '') {
        return;
    }
    const date = new Date(raw);
    return Number.isNaN(date.getTime()) ? undefined : date;
};

const toFieldErrorType = (raw: unknown): FieldErrorType =>
    fromEnum(raw, FIELD_ERROR_TYPE_PREFIX, KNOWN_FIELD_ERROR_TYPES) as unknown as FieldErrorType;

interface WireEnvelope<V> {
    ttl?: number | null;
    value?: V | null;
    error?: { type?: string | null; updated_at?: string | null } | null;
}

const toFieldError = (raw: WireEnvelope<unknown>['error']): FieldError | null => {
    if (raw == null) {
        return null;
    }
    return {
        type: toFieldErrorType(raw.type),
        updatedAt: toDate(raw.updated_at) ?? null,
    };
};

const toMonitoredField = <V, D>(
    envelope: WireEnvelope<V> | null | undefined,
    mapValue: (value: V) => D | undefined,
    updatedAtOf: (value: V) => string | null | undefined
): MonitoredField<D> => {
    if (envelope == null) {
        return {};
    }
    const ttl = envelope.ttl ?? null;
    const error = toFieldError(envelope.error);
    const rawValue = envelope.value;
    if (rawValue == null) {
        return { ttl, value: null, error };
    }
    const mapped = mapValue(rawValue);
    if (mapped === undefined) {
        return { ttl, value: null, error };
    }
    return {
        ttl,
        value: { value: mapped, updatedAt: toDate(updatedAtOf(rawValue)) ?? null },
        error,
    };
};

const serializeMonitoredField = <T>(field: MonitoredField<T>): MonitoredField<T, string> => ({
    ...field,
    value:
        field.value == null
            ? field.value
            : {
                  ...field.value,
                  updatedAt: field.value.updatedAt?.toISOString() ?? null,
              },
    error:
        field.error == null
            ? field.error
            : {
                  ...field.error,
                  updatedAt: field.error.updatedAt?.toISOString() ?? null,
              },
});

const toGeoPosition = (
    raw:
        | {
              latitude?: number | null;
              longitude?: number | null;
              heading_degrees?: number | null;
              altitude_meters?: number | null;
          }
        | null
        | undefined
): GeoPosition | undefined => {
    if (raw == null || typeof raw.latitude !== 'number' || typeof raw.longitude !== 'number') {
        return;
    }
    const position: GeoPosition = { lat: raw.latitude, lng: raw.longitude };
    if (typeof raw.heading_degrees === 'number') {
        position.heading = raw.heading_degrees;
    }
    if (typeof raw.altitude_meters === 'number') {
        position.altitudeMeters = raw.altitude_meters;
    }
    return position;
};

const toDeviation = (
    raw:
        | {
              id?: string | null;
              description?: string | null;
              is_blocking?: boolean | null;
              severity?: string | null;
              state?: string | null;
              sent_at?: string | null;
          }
        | null
        | undefined
): Deviation | undefined => {
    if (raw == null) {
        return;
    }
    const state: DeviationState = fromEnum(raw.state, DEVIATION_STATE_PREFIX, DEVIATION_STATES);
    return {
        id: raw.id ?? '',
        description: raw.description ?? '',
        isBlocking: raw.is_blocking ?? false,
        severity: fromEnum<DeviationSeverity>(raw.severity, SEVERITY_PREFIX, DEVIATION_SEVERITIES),
        state,
        sentAt: toDate(raw.sent_at) ?? null,
        isActive: state === 'ACTIVE',
    };
};

const toOwner = (raw: {
    id?: string | null;
    full_name?: string | null;
    email?: string | null;
    phone_number?: string | null;
    position?: string | null;
}): VehicleOwner => ({
    id: raw.id ?? '',
    fullName: raw.full_name ?? '',
    email: raw.email ?? undefined,
    phoneNumber: raw.phone_number ?? undefined,
    position: raw.position ?? undefined,
});

export const vehicleFromRestV2 = (raw: unknown): Vehicle => {
    /*Double assertion (`as unknown as WireVehicle`) is required because the
    `safeField`/`safeOptional` wrappers replace each field with a
    `catchWithLog(schema.nullish(), fallback)` schema, so `z.infer` of
     WireVehicleSchema no longer structurally matches `VehicleTO`.
     guarded by tests/zodVersionAlignment.test.ts.*/
    const wire = WireVehicleSchema.parse(raw) as unknown as WireVehicle;

    const deviations: MonitoredField<Deviation>[] = (wire.deviations ?? []).map(entry =>
        toMonitoredField(
            entry,
            value => toDeviation(value.value),
            value => value.updated_at
        )
    );

    const hasBlockingDeviation = deviations.some(
        field => field.value?.value?.isActive === true && field.value?.value?.isBlocking === true
    );

    return {
        vin: wire.vin,
        name: wire.name ?? '',
        licensePlate: wire.license_plate ?? undefined,
        fleetId: wire.fleet_id ?? undefined,
        activeMissionId: wire.active_mission_id ?? undefined,
        owners: (wire.owners ?? []).map(toOwner),
        latestVehicleUpdate: toDate(wire.latest_vehicle_update) ?? null,
        timestamp: toDate(wire.timestamp) ?? null,
        ads: toMonitoredField(
            wire.ads_state,
            value => fromEnum<AdsState>(value.state, ADS_STATE_PREFIX, ADS_STATES),
            value => value.updated_at
        ),
        connectivity: toMonitoredField<
            NonNullable<NonNullable<typeof wire.connectivity_status>['value']>,
            ConnectivityDetails
        >(
            wire.connectivity_status,
            value => ({
                state: fromEnum(value.state, CONNECTIVITY_STATE_PREFIX, CONNECTIVITY_STATES),
                expectDisconnection: value.expect_disconnection ?? undefined,
                latency: value.latency ?? undefined,
            }),
            value => value.updated_at
        ),
        drivingState: toMonitoredField(
            wire.driving_state,
            value => fromEnum<DrivingState>(value.state, DRIVING_STATE_PREFIX, DRIVING_STATES),
            value => value.updated_at
        ),
        driverMode: toMonitoredField(
            wire.driver_mode,
            value => fromEnum<DriverMode>(value.mode, DRIVER_MODE_PREFIX, DRIVER_MODES),
            value => value.updated_at
        ),
        position: toMonitoredField(
            wire.position,
            value => toGeoPosition(value.position),
            value => value.updated_at
        ),
        speedKmh: toMonitoredField(
            wire.speed_ms,
            value => (typeof value.speed_ms === 'number' ? value.speed_ms * 3.6 : undefined),
            value => value.updated_at
        ),
        mrm: toMonitoredField<NonNullable<NonNullable<typeof wire.mrm_state>['value']>, MrmDetails>(
            wire.mrm_state,
            value => ({
                state: fromEnum<MrmState>(value.data?.state, MRM_STATE_PREFIX, MRM_STATES),
                causes: value.data?.causes ?? [],
            }),
            value => value.updated_at
        ),
        deviations,
        hasBlockingDeviation,
    };
};

export const vehicleSummaryFromRestV2 = (raw: unknown): VehicleSummary => {
    const wire = WireVehicleSummarySchema.parse(raw) as WireVehicleSummary;

    const connectivity = toMonitoredField<
        NonNullable<NonNullable<typeof wire.connectivity_status>['value']>,
        ConnectivityDetails
    >(
        wire.connectivity_status,
        value => ({
            state: fromEnum(value.state, CONNECTIVITY_STATE_PREFIX, CONNECTIVITY_STATES),
            expectDisconnection: value.expect_disconnection ?? undefined,
            latency: value.latency ?? undefined,
        }),
        value => value.updated_at
    );

    return {
        vin: wire.vin,
        fleetId: wire.fleet_id ?? undefined,
        name: wire.name ?? wire.vin,
        connectivity: serializeMonitoredField(connectivity),
        type: fromPlainEnum<VehicleType>(wire.type, VEHICLE_TYPES),
        subtype: fromPlainEnum<VehicleSubtype>(wire.subtype, VEHICLE_SUBTYPES),
        createdAt: wire.created_at ?? undefined,
        licensePlate: wire.license_plate ?? undefined,
        createdBy: wire.created_by ?? undefined,
    };
};
